import moment from 'moment';
import { FieldTypes } from '../helpers/constants';
import { formatUtcDateAsLocalDate } from '../helpers/DateTimeUtility';
import { isGuid } from '../helpers/utility';

export const defaultColumnName = 'Unnamed Column';

export const maxColumnLength = 100;

export const metadataColumns = [
  'FormId',
  'FormName',
  'CreatedDate',
  'LastUpdatedDate',
  'CompletedDate',
  'Status',
  'TemplateId',
  'TemplateName',
  'OwnerId',
  'OwnerName',
  'OwnerPhoneNumber',
  'CreatedById',
  'CreatedByName',
  'LastUpdatedById',
  'LastUpdatedByName',
  'LatitudeLongitude'
];

export const getFormattedMetaColumnName = (metaColumnName) => {
  switch (metaColumnName) {
    case 'FormId':
      return 'Form Id';
    case 'FormName':
      return 'Form Name';
    case 'CreatedDate':
      return 'Created Date';
    case 'LastUpdatedDate':
      return 'Last Updated Date';
    case 'CompletedDate':
      return 'Completed Date';
    case 'Status':
      return 'Status';
    case 'TemplateId':
      return 'Template Id';
    case 'TemplateName':
      return 'Template Name';
    case 'OwnerId':
      return 'Owner Id';
    case 'OwnerName':
      return 'Owner Name';
    case 'OwnerPhoneNumber':
      return 'Owner Phone Number';
    case 'CreatedById':
      return 'Created By Id';
    case 'CreatedByName':
      return 'Created By Name';
    case 'LastUpdatedById':
      return 'Last Updated By Id';
    case 'LastUpdatedByName':
      return 'Last Updated By Name';
    case 'LatitudeLongitude':
      return 'Location';
    default:
      return null;
  }
};

export const supportedReportFieldTypes = [
  FieldTypes.TextBox,
  FieldTypes.DropDown,
  FieldTypes.TextArea,
  FieldTypes.CheckBox,
  FieldTypes.DateTime,
  FieldTypes.Date,
  FieldTypes.Time,
  FieldTypes.CheckBoxGroup,
  FieldTypes.Number,
  FieldTypes.Database,
  FieldTypes.AutoNumber,
  FieldTypes.Barcode
];

export function reportIsValid(reportView) {
  // ensure at least one column
  if (!reportView.columns || reportView.columns.length === 0) {
    return false;
  }
  // ensure at least one template
  if (!reportView.templates || reportView.templates.length === 0) {
    return false;
  }

  // return true if any column is mapped to metadata
  for (const column of reportView.columns) {
    if (column.metaColumnName) {
      return true;
    }
  }

  // return true if any template has a columnMapping
  for (const template of reportView.templates) {
    if (template.columnMap && template.columnMap.length > 0) {
      return true;
    }
  }
  return false;
}

export function getColumnType(columnId, reportView) {
  let metaColumnName = null;

  if (isGuid(columnId)) {
    const column = reportView.columns.find((c) => c.columnId === columnId);
    metaColumnName = column ? column.metaColumnName : null;
  } else {
    metaColumnName = columnId;
  }

  if (metaColumnName) {
    switch (metaColumnName.toLowerCase()) {
      case 'createddate':
      case 'lastupdateddate':
      case 'completeddate':
        return 'date';

      case 'formid':
      case 'templateid':
      case 'ownerid':
      case 'createdbyid':
      case 'lastupdatedbyid':
        return 'guid';

      default:
        return 'text';
    }
  }

  const fieldTypes = reportView.templates
    .map((template) => {
      const columnMapping = template.columnMap.find((mapping) => mapping.columnId === columnId);
      return columnMapping ? columnMapping.fieldType : null;
    })
    .filter((type) => type !== null);

  if (fieldTypes.every((fieldType) => fieldType === FieldTypes.TextBox)) {
    return 'text';
  } else if (fieldTypes.every((fieldType) => fieldType === FieldTypes.DateTime || fieldType === FieldTypes.Date)) {
    return 'date';
  } else if (fieldTypes.every((fieldType) => fieldType === FieldTypes.CheckBox)) {
    return 'checkbox';
  } else if (fieldTypes.every((fieldType) => fieldType === FieldTypes.Number)) {
    return 'number';
  }

  return 'text';
}

export function getColumnTypeForFieldType(fieldType) {
  switch (fieldType) {
    case FieldTypes.Date:
    case FieldTypes.DateTime:
      return 'date';
    case FieldTypes.CheckBox:
      return 'checkbox';
    case FieldTypes.Number:
      return 'number';
    default:
      return 'text';
  }
}

export function checkIfColumnUsedInFilters(columnId, filters) {
  if (filters) {
    for (const filter of filters) {
      if (filter.filters && filter.filters.length > 0) {
        const childFiltersUsed = checkIfColumnUsedInFilters(columnId, filter.filters);
        if (childFiltersUsed) {
          return true;
        }
      } else if (filter.columnId === columnId) {
        return true;
      }
    }
  }
  return false;
}

export function parseDateRange(dateRange) {
  const splitIndex = dateRange.indexOf(',');
  const fromDate = dateRange.substring(0, splitIndex);
  const toDate = dateRange.substring(splitIndex + 1);

  return `${moment(fromDate).isValid() ? formatUtcDateAsLocalDate(fromDate) : ''} - ${moment(toDate).isValid() ? formatUtcDateAsLocalDate(toDate) : ''}`;
}
